import React, { useState, useEffect, useCallback, useRef } from 'react';
import CommandCenterPickerModal from '../shared/CommandCenterPickerModal';
import '../../css/TaskModal.css';

const PRIORITY_OPTIONS = [
    { value: 'NONE', label: 'None' },
    { value: 'LOW', label: 'Low' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'HIGH', label: 'High' },
    { value: 'URGENT', label: 'Urgent' },
];

const TASK_TYPE_OPTIONS = [
    { value: 'TASK', label: 'Task', color: null },
    { value: 'STORY', label: 'Story', color: '#3B82F6' },
    { value: 'EPIC', label: 'Epic', color: '#8B5CF6' },
];

const TaskModal = ({
    task, columns, boardAbbreviation, existingLabels = [], boardMembers = [],
    onClose, onUpdate, onDelete, onMove, onArchive, readOnly = false,
    // Comment props
    boardId, apiCall, currentUserUid, canComment = false, showSignInToComment = false,
    // BBTM-37 submission/review props
    mode = 'edit', onSubmit, onApprove, onDeny,
    onImmediateUpdate,
}) => {
    const isSubmit = mode === 'submit';
    const isReview = mode === 'review';
    const isEdit = mode === 'edit';
    const [title, setTitle] = useState(task.title);
    const [description, setDescription] = useState(task.description || '');
    const [priority, setPriority] = useState(task.priority);
    const [dueDate, setDueDate] = useState(
        task.due_date ? task.due_date.slice(0, 16) : ''
    );
    const [labelInput, setLabelInput] = useState('');
    const [labels, setLabels] = useState(task.labels || []);
    const [showDropdown, setShowDropdown] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [editingTitle, setEditingTitle] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [assigneeId, setAssigneeId] = useState(task.assignee_info ? task.assignee_info.id : '');
    const [taskType, setTaskType] = useState(task.task_type || 'TASK');

    // ─── Submission / review state (BBTM-37) ─────────────────────────
    const [columnId, setColumnId] = useState(task.column || (columns[0] && columns[0].id) || '');
    // Keep the select in sync if the parent later supplies a task with a new column.
    useEffect(() => {
        if (task.column != null) setColumnId(task.column);
    }, [task.column]);
    const [actionLoading, setActionLoading] = useState(false);
    const [reviewNote, setReviewNote] = useState('');
    const [submitDone, setSubmitDone] = useState(false);

    const handleSubmitProposal = async () => {
        if (!title.trim() || actionLoading || !onSubmit) return;
        setActionLoading(true);
        const ok = await onSubmit({
            title: title.trim(),
            description,
            priority,
            due_date: dueDate || null,
            labels,
            task_type: taskType,
            suggested_column_id: null,
        });
        setActionLoading(false);
        if (ok) setSubmitDone(true);
    };

    const buildReviewFields = () => ({
        column_id: columnId,
        title: title.trim(),
        description,
        priority,
        due_date: dueDate || null,
        labels,
        task_type: taskType,
    });

    const handleApprove = async () => {
        if (!title.trim() || !columnId || actionLoading || !onApprove) return;
        setActionLoading(true);
        await onApprove(task.id, buildReviewFields());
        setActionLoading(false);
    };

    const handleDeny = async () => {
        if (actionLoading || !onDeny) return;
        setActionLoading(true);
        await onDeny(task.id, reviewNote.trim());
        setActionLoading(false);
    };

    // ─── Hierarchy state ─────────────────────────────────────────────
    const [parentTask, setParentTask] = useState(task.parent_task_info || null);
    const [parentLoading, setParentLoading] = useState(false);
    const [children, setChildren] = useState([]);
    const [childrenLoading, setChildrenLoading] = useState(false);
    const [parentSearch, setParentSearch] = useState('');
    const [parentSearchResults, setParentSearchResults] = useState([]);
    const [parentSearchLoading, setParentSearchLoading] = useState(false);
    const [showParentDropdown, setShowParentDropdown] = useState(false);
    const [settingParent, setSettingParent] = useState(false);
    const [removingParent, setRemovingParent] = useState(false);

    const loadChildren = useCallback(async () => {
        if (mode !== 'edit' || !boardId || !apiCall) return;
        setChildrenLoading(true);
        try {
            const res = await apiCall(`/tasks/boards/${boardId}/tasks/${task.id}/children/`);
            if (res.ok) {
                const data = await res.json();
                setChildren(data);
            }
        } catch {}
        setChildrenLoading(false);
    }, [boardId, task.id, apiCall, mode]);

    useEffect(() => { loadChildren(); }, [loadChildren]);

    useEffect(() => {
        if (!parentSearch.trim() || parentSearch.length < 2 || !apiCall) {
            setParentSearchResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            setParentSearchLoading(true);
            try {
                const res = await apiCall(`/tasks/search/?q=${encodeURIComponent(parentSearch)}`);
                if (res.ok) {
                    const data = await res.json();
                    const excludeIds = new Set([task.id, ...children.map(c => c.id)]);
                    if (parentTask) excludeIds.add(parentTask.id);
                    setParentSearchResults(data.filter(t => !excludeIds.has(t.id)));
                }
            } catch {}
            setParentSearchLoading(false);
        }, 400);
        return () => clearTimeout(timer);
    }, [parentSearch, task.id, parentTask, children, apiCall]);

    const handleSetParent = async (targetTask) => {
        if (settingParent) return;
        setSettingParent(true);
        setShowParentDropdown(false);
        setParentSearch('');
        setParentSearchResults([]);
        try {
            const res = await apiCall(`/tasks/boards/${boardId}/tasks/${task.id}/parent/`, {
                method: 'POST',
                body: JSON.stringify({ parent_task_id: targetTask.id }),
            });
            if (res.ok) {
                const data = await res.json();
                setParentTask(data);
                if (onImmediateUpdate) onImmediateUpdate({ parent_task_info: data });
            }
        } catch {}
        setSettingParent(false);
    };

    const handleRemoveParent = async () => {
        if (removingParent) return;
        setRemovingParent(true);
        try {
            const res = await apiCall(`/tasks/boards/${boardId}/tasks/${task.id}/parent/`, {
                method: 'DELETE',
            });
            if (res.ok) {
                setParentTask(null);
                if (onImmediateUpdate) onImmediateUpdate({ parent_task_info: null });
            }
        } catch {}
        setRemovingParent(false);
    };

    const handleOpenHierarchyTask = (t) => {
        window.open(`/board/${t.board_id}/task/${t.board_number}`, '_blank');
    };

    // ─── Linked tasks state ──────────────────────────────────────────
    const [linkedTasks, setLinkedTasks] = useState([]);
    const [linksLoading, setLinksLoading] = useState(false);
    const [linkSearch, setLinkSearch] = useState('');
    const [linkSearchResults, setLinkSearchResults] = useState([]);
    const [linkSearchLoading, setLinkSearchLoading] = useState(false);
    const [showLinkDropdown, setShowLinkDropdown] = useState(false);
    const [unlinkingId, setUnlinkingId] = useState(null);
    const [linking, setLinking] = useState(false);

    const loadLinks = useCallback(async () => {
        if (mode !== 'edit' || !boardId || !apiCall) return;
        setLinksLoading(true);
        try {
            const res = await apiCall(`/tasks/boards/${boardId}/tasks/${task.id}/links/`);
            if (res.ok) {
                const data = await res.json();
                setLinkedTasks(data);
            }
        } catch {}
        setLinksLoading(false);
    }, [boardId, task.id, apiCall, mode]);

    useEffect(() => { loadLinks(); }, [loadLinks]);

    useEffect(() => {
        if (!linkSearch.trim() || linkSearch.length < 2 || !apiCall) {
            setLinkSearchResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            setLinkSearchLoading(true);
            try {
                const res = await apiCall(`/tasks/search/?q=${encodeURIComponent(linkSearch)}`);
                if (res.ok) {
                    const data = await res.json();
                    const linkedIds = new Set([task.id, ...linkedTasks.map(t => t.id)]);
                    setLinkSearchResults(data.filter(t => !linkedIds.has(t.id)));
                }
            } catch {}
            setLinkSearchLoading(false);
        }, 400);
        return () => clearTimeout(timer);
    }, [linkSearch, linkedTasks, task.id, apiCall]);

    const handleAddLink = async (targetTask) => {
        if (linking) return;
        setLinking(true);
        setShowLinkDropdown(false);
        setLinkSearch('');
        setLinkSearchResults([]);
        try {
            const res = await apiCall(`/tasks/boards/${boardId}/tasks/${task.id}/links/`, {
                method: 'POST',
                body: JSON.stringify({ linked_task_id: targetTask.id }),
            });
            if (res.ok) {
                const data = await res.json();
                setLinkedTasks(prev => [...prev, data]);
            }
        } catch {}
        setLinking(false);
    };

    const handleRemoveLink = async (linkedTaskId) => {
        if (unlinkingId) return;
        setUnlinkingId(linkedTaskId);
        try {
            const res = await apiCall(`/tasks/boards/${boardId}/tasks/${task.id}/links/${linkedTaskId}/`, {
                method: 'DELETE',
            });
            if (res.ok) {
                setLinkedTasks(prev => prev.filter(t => t.id !== linkedTaskId));
            }
        } catch {}
        setUnlinkingId(null);
    };

    const handleOpenLinkedTask = (lt) => {
        window.open(`/board/${lt.board_id}/task/${lt.board_number}`, '_blank');
    };

    // ─── Watch state (BBTM-31) ───────────────────────────────────────
    const [isWatching, setIsWatching] = useState(task.is_watching || false);
    const [watchersCount, setWatchersCount] = useState(task.watchers_count || 0);
    const [watchLoading, setWatchLoading] = useState(false);

    const handleToggleWatch = async () => {
        if (!apiCall || watchLoading) return;
        setWatchLoading(true);
        try {
            const method = isWatching ? 'DELETE' : 'POST';
            const endpoint = isWatching
                ? `/tasks/boards/${boardId}/tasks/${task.id}/unwatch/`
                : `/tasks/boards/${boardId}/tasks/${task.id}/watch/`;
            const res = await apiCall(endpoint, { method });
            if (res.ok) {
                const data = await res.json();
                setIsWatching(data.watching);
                setWatchersCount(data.watchers_count);
                if (onImmediateUpdate) onImmediateUpdate({ is_watching: data.watching, watchers_count: data.watchers_count });
            }
        } catch {
            // silently fail
        }
        setWatchLoading(false);
    };

    // ─── Comments state ──────────────────────────────────────────────
    const [comments, setComments] = useState([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [commentInput, setCommentInput] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const [deletingCommentId, setDeletingCommentId] = useState(null);

    const loadComments = useCallback(async () => {
        if (mode !== 'edit' || !boardId || !apiCall) return;
        setCommentsLoading(true);
        try {
            const res = await apiCall(`/tasks/boards/${boardId}/tasks/${task.id}/comments/`);
            if (res.ok) {
                const data = await res.json();
                setComments(data);
            }
        } catch {
            // silently fail; comments are non-critical
        }
        setCommentsLoading(false);
    }, [boardId, task.id, apiCall, mode]);

    useEffect(() => {
        loadComments();
    }, [loadComments]);

    const handleSubmitComment = async () => {
        const content = commentInput.trim();
        if (!content || submittingComment || !apiCall) return;
        setSubmittingComment(true);
        try {
            const res = await apiCall(`/tasks/boards/${boardId}/tasks/${task.id}/comments/`, {
                method: 'POST',
                body: JSON.stringify({ content }),
            });
            if (res.ok) {
                const newComment = await res.json();
                setComments(prev => [...prev, newComment]);
                setCommentInput('');
            }
        } catch {
            // silently fail
        }
        setSubmittingComment(false);
    };

    const handleDeleteComment = async (commentId) => {
        if (deletingCommentId) return;
        setDeletingCommentId(commentId);
        try {
            const res = await apiCall(`/tasks/boards/${boardId}/tasks/${task.id}/comments/${commentId}/delete/`, {
                method: 'DELETE',
            });
            if (res.ok) {
                setComments(prev => prev.filter(c => c.id !== commentId));
            }
        } catch {
            // silently fail
        }
        setDeletingCommentId(null);
    };

    // ─── Attachments state ───────────────────────────────────────────
    const [attachments, setAttachments] = useState([]);
    const [attachmentsLoading, setAttachmentsLoading] = useState(false);
    const [uploadingAttachment, setUploadingAttachment] = useState(false);
    const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
    const [attachmentError, setAttachmentError] = useState('');
    const [thumbnails, setThumbnails] = useState({}); // attachment id -> object URL
    const [ccPickerOpen, setCcPickerOpen] = useState(false);
    const fileInputRef = useRef(null);
    const thumbnailsRef = useRef({});

    useEffect(() => { thumbnailsRef.current = thumbnails; }, [thumbnails]);
    // Revoke all generated object URLs when the modal unmounts
    useEffect(() => () => {
        Object.values(thumbnailsRef.current).forEach(url => URL.revokeObjectURL(url));
    }, []);

    const loadAttachments = useCallback(async () => {
        if (mode !== 'edit' || !boardId || !apiCall) return;
        setAttachmentsLoading(true);
        try {
            const res = await apiCall(`/tasks/boards/${boardId}/tasks/${task.id}/attachments/`);
            if (res.ok) {
                setAttachments(await res.json());
            }
        } catch {
            // silently fail; attachments are non-critical
        }
        setAttachmentsLoading(false);
    }, [boardId, task.id, apiCall, mode]);

    useEffect(() => { loadAttachments(); }, [loadAttachments]);

    // Lazily fetch thumbnails for image attachments (auth-protected, so via blob)
    useEffect(() => {
        attachments.forEach(att => {
            if (att.is_image && !thumbnailsRef.current[att.id]) {
                apiCall(`/tasks/boards/${boardId}/tasks/${task.id}/attachments/${att.id}/`)
                    .then(res => (res.ok ? res.blob() : null))
                    .then(blob => {
                        if (blob) {
                            const url = URL.createObjectURL(blob);
                            setThumbnails(prev => ({ ...prev, [att.id]: url }));
                        }
                    })
                    .catch(() => {});
            }
        });
    }, [attachments, boardId, task.id, apiCall]);

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const iconForAttachment = (att) => {
        const type = att.file_type || '';
        const name = (att.file_name || '').toLowerCase();
        if (type === 'application/pdf' || name.endsWith('.pdf')) return 'fa-file-pdf';
        if (type === 'application/zip' || name.endsWith('.zip')) return 'fa-file-archive';
        if (name.endsWith('.csv') || name.endsWith('.xlsx')) return 'fa-file-excel';
        if (name.endsWith('.docx')) return 'fa-file-word';
        if (name.endsWith('.txt')) return 'fa-file-alt';
        if (type.startsWith('image/')) return 'fa-file-image';
        return 'fa-file';
    };

    const handleFileSelected = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = ''; // reset so the same file can be re-selected
        await uploadAttachmentFile(file);
    };

    const uploadAttachmentFile = async (file) => {
        if (!file || uploadingAttachment || !apiCall) return;
        setAttachmentError('');
        if (file.size > 25 * 1024 * 1024) {
            setAttachmentError('File too large. Maximum size: 25MB');
            return;
        }
        setUploadingAttachment(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await apiCall(`/tasks/boards/${boardId}/tasks/${task.id}/attachments/`, {
                method: 'POST',
                body: formData,
            });
            if (res.ok) {
                const newAtt = await res.json();
                setAttachments(prev => [newAtt, ...prev]);
                if (onImmediateUpdate) onImmediateUpdate({ attachments_count: attachments.length + 1 });
            } else {
                let msg = 'Upload failed.';
                try { const body = await res.json(); msg = body.error || msg; } catch {}
                setAttachmentError(msg);
            }
        } catch {
            setAttachmentError('Upload failed.');
        }
        setUploadingAttachment(false);
    };

    const handleDownloadAttachment = async (att) => {
        if (!apiCall) return;
        try {
            const res = await apiCall(`/tasks/boards/${boardId}/tasks/${task.id}/attachments/${att.id}/`);
            if (!res.ok) return;
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = att.file_name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            // silently fail
        }
    };

    const handleDeleteAttachment = async (attId) => {
        if (deletingAttachmentId) return;
        setDeletingAttachmentId(attId);
        try {
            const res = await apiCall(`/tasks/boards/${boardId}/tasks/${task.id}/attachments/${attId}/delete/`, {
                method: 'DELETE',
            });
            if (res.ok) {
                setAttachments(prev => prev.filter(a => a.id !== attId));
                if (onImmediateUpdate) onImmediateUpdate({ attachments_count: Math.max(0, attachments.length - 1) });
            }
        } catch {
            // silently fail
        }
        setDeletingAttachmentId(null);
    };

    const formatCommentDate = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        const diffHrs = Math.floor(diffMins / 60);
        if (diffHrs < 24) return `${diffHrs}h ago`;
        const diffDays = Math.floor(diffHrs / 24);
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const boardTaskId = task.board_task_id || (
        boardAbbreviation
            ? `${boardAbbreviation}-${task.board_number || task.id}`
            : `#${task.board_number || task.id}`
    );

    // Track changes without auto-saving
    const markChanged = () => setHasChanges(true);

    const handleSaveAll = async () => {
        setSaving(true);
        const updates = {};
        if (title.trim() && title !== task.title) updates.title = title.trim();
        if (description !== (task.description || '')) updates.description = description;
        if (priority !== task.priority) updates.priority = priority;
        if (dueDate !== (task.due_date ? task.due_date.slice(0, 16) : '')) {
            updates.due_date = dueDate || null;
        }
        if (JSON.stringify(labels) !== JSON.stringify(task.labels || [])) updates.labels = labels;
        const currentAssigneeId = task.assignee_info ? task.assignee_info.id : '';
        if (assigneeId !== currentAssigneeId) {
            updates.assignee_id = assigneeId || null;
        }
        if (taskType !== (task.task_type || 'TASK')) updates.task_type = taskType;

        if (Object.keys(updates).length > 0) {
            await onUpdate(task.id, updates);
        }
        setHasChanges(false);
        setSaving(false);
    };

    const handleSaveTitle = () => {
        setEditingTitle(false);
        if (title.trim() && title !== task.title) markChanged();
    };

    const handleSaveDescription = () => {
        setEditingDesc(false);
        if (description !== task.description) markChanged();
    };

    const handlePriorityChange = (newPriority) => {
        setPriority(newPriority);
        markChanged();
    };

    const handleDueDateChange = (e) => {
        setDueDate(e.target.value);
        markChanged();
    };

    const filteredSuggestions = existingLabels.filter(
        l => !labels.includes(l) && l.toLowerCase().includes(labelInput.toLowerCase())
    );

    const handleAddLabel = () => {
        const label = labelInput.trim();
        if (label && !labels.includes(label)) {
            setLabels(prev => [...prev, label]);
            setLabelInput('');
            setShowDropdown(false);
            setHighlightedIndex(-1);
            markChanged();
        }
    };

    const handleSelectSuggestion = (suggestion) => {
        if (!labels.includes(suggestion)) {
            setLabels(prev => [...prev, suggestion]);
            markChanged();
        }
        setLabelInput('');
        setShowDropdown(false);
        setHighlightedIndex(-1);
    };

    const handleLabelKeyDown = (e) => {
        if (showDropdown && filteredSuggestions.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlightedIndex(i => Math.min(i + 1, filteredSuggestions.length - 1));
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlightedIndex(i => Math.max(i - 1, -1));
                return;
            }
            if (e.key === 'Enter') {
                if (highlightedIndex >= 0) {
                    handleSelectSuggestion(filteredSuggestions[highlightedIndex]);
                } else {
                    handleAddLabel();
                }
                return;
            }
            if (e.key === 'Escape') {
                setShowDropdown(false);
                setHighlightedIndex(-1);
                return;
            }
        }
        if (e.key === 'Enter') handleAddLabel();
    };

    const handleRemoveLabel = (labelToRemove) => {
        setLabels(prev => prev.filter(l => l !== labelToRemove));
        markChanged();
    };

    const handleColumnChange = (newColumnId) => {
        const parsed = parseInt(newColumnId, 10);
        setColumnId(parsed); // optimistically reflect the move in the select
        if (onMove) onMove(task.id, parsed, 0);
    };

    return (
        <div className="TaskModal__overlay" onClick={onClose}>
            <div className="TaskModal" onClick={(e) => e.stopPropagation()}>
                <div className="TaskModal__header">
                    <div className="TaskModal__header-ids">
                        {isEdit ? (
                            <>
                                <span className="TaskModal__board-task-id">{boardTaskId}</span>
                                <span className="TaskModal__task-id">ID: {task.id}</span>
                            </>
                        ) : (
                            <span className="TaskModal__board-task-id">
                                {isSubmit ? 'New Task Proposal' : 'Review Submission'}
                            </span>
                        )}
                    </div>
                    <div className="TaskModal__header-actions">
                        {isEdit && apiCall && (
                            <button
                                className={`TaskModal__watch-btn${isWatching ? ' TaskModal__watch-btn--watching' : ''}`}
                                onClick={handleToggleWatch}
                                disabled={watchLoading}
                                title={isWatching ? 'Stop watching this task' : 'Watch this task for updates'}
                            >
                                <i className="fas fa-eye" />
                                <span>{isWatching ? 'Watching' : 'Watch'}</span>
                                {watchersCount > 0 && (
                                    <span className="TaskModal__watch-count">{watchersCount}</span>
                                )}
                            </button>
                        )}
                        <button className="TaskModal__close" onClick={onClose}>×</button>
                    </div>
                </div>

                {/* Submitter banner (review mode) */}
                {isReview && (
                    <div className="TaskModal__submission-banner">
                        <i className="fas fa-user-plus" />
                        <span>
                            Proposed by <strong>{task.submitted_by_info?.name || task.submitted_by_info?.email || 'a visitor'}</strong>
                            {task.suggested_column_name && <> · suggested <strong>{task.suggested_column_name}</strong></>}
                        </span>
                    </div>
                )}

                {/* Title */}
                <div className="TaskModal__section">
                    {isSubmit ? (
                        <input
                            className="TaskModal__title-input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="What needs to be done?"
                            autoFocus
                        />
                    ) : !readOnly && editingTitle ? (
                        <input
                            className="TaskModal__title-input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            onBlur={handleSaveTitle}
                            onKeyDown={(e) => e.key === 'Enter' && handleSaveTitle()}
                            autoFocus
                        />
                    ) : (
                        <h2
                            className="TaskModal__title"
                            onClick={() => !readOnly && setEditingTitle(true)}
                            style={readOnly ? { cursor: 'default' } : {}}
                        >
                            {title}
                        </h2>
                    )}
                </div>

                {/* Column */}
                {!isSubmit && (
                <div className="TaskModal__section">
                    <label className="TaskModal__label">
                        {isReview ? 'Place in column' : 'Column'}
                    </label>
                    <select
                        className="TaskModal__select"
                        value={columnId}
                        onChange={(e) => isEdit ? handleColumnChange(e.target.value) : setColumnId(parseInt(e.target.value, 10))}
                        disabled={readOnly}
                    >
                        {columns.map(col => (
                            <option key={col.id} value={col.id}>{col.name}</option>
                        ))}
                    </select>
                </div>
                )}

                {/* Task Type */}
                {!isSubmit && (
                <div className="TaskModal__section">
                    <label className="TaskModal__label">Type</label>
                    <div className="TaskModal__type-options">
                        {TASK_TYPE_OPTIONS.map(opt => (
                            <button
                                key={opt.value}
                                className={`TaskModal__type-btn TaskModal__type-btn--${opt.value.toLowerCase()}${taskType === opt.value ? ' active' : ''}`}
                                onClick={() => { if (!readOnly) { setTaskType(opt.value); markChanged(); } }}
                                disabled={readOnly}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
                )}

                {/* Parent Task */}
                {isEdit && boardId && apiCall && (
                <div className="TaskModal__section TaskModal__hierarchy-section">
                    <label className="TaskModal__label">Parent</label>
                    {parentLoading ? (
                        <div className="TaskModal__comments-loading">
                            <span className="btn-spinner btn-spinner--accent" />
                        </div>
                    ) : (
                        <>
                            {parentTask ? (
                                <div className="TaskModal__linked-item">
                                    <button
                                        className="TaskModal__linked-open"
                                        onClick={() => handleOpenHierarchyTask(parentTask)}
                                        title={`Open ${parentTask.board_task_id} in new tab`}
                                    >
                                        <span className={`TaskModal__hierarchy-type-badge TaskModal__hierarchy-type-badge--${(parentTask.task_type || 'task').toLowerCase()}`}>
                                            {parentTask.task_type || 'Task'}
                                        </span>
                                        <span className="TaskModal__linked-id">{parentTask.board_task_id}</span>
                                        <span className="TaskModal__linked-title">{parentTask.title}</span>
                                        <i className="fas fa-external-link-alt TaskModal__linked-ext" />
                                    </button>
                                    {!readOnly && (
                                        <button
                                            className="TaskModal__linked-remove"
                                            onClick={handleRemoveParent}
                                            disabled={removingParent}
                                            title="Remove parent"
                                        >
                                            {removingParent
                                                ? <span className="btn-spinner" style={{ width: 10, height: 10 }} />
                                                : <i className="fas fa-times" />}
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <p className="TaskModal__comments-empty">No parent task.</p>
                            )}
                            {!readOnly && !parentTask && (
                                <div className="TaskModal__link-input-wrapper">
                                    <input
                                        className="TaskModal__label-input"
                                        value={parentSearch}
                                        onChange={(e) => { setParentSearch(e.target.value); setShowParentDropdown(true); }}
                                        placeholder="Search for a parent task..."
                                        onFocus={() => setShowParentDropdown(true)}
                                        onBlur={() => setTimeout(() => setShowParentDropdown(false), 200)}
                                        autoComplete="off"
                                        disabled={settingParent}
                                    />
                                    {parentSearchLoading && (
                                        <span className="TaskModal__link-search-spinner btn-spinner btn-spinner--accent" />
                                    )}
                                    {showParentDropdown && parentSearchResults.length > 0 && (
                                        <div className="TaskModal__label-dropdown">
                                            {parentSearchResults.map(result => (
                                                <div
                                                    key={result.id}
                                                    className="TaskModal__link-dropdown-item"
                                                    onMouseDown={() => handleSetParent(result)}
                                                >
                                                    <span className={`TaskModal__hierarchy-type-badge TaskModal__hierarchy-type-badge--${(result.task_type || 'task').toLowerCase()}`}>
                                                        {result.task_type || 'Task'}
                                                    </span>
                                                    <span className="TaskModal__linked-id">{result.board_task_id}</span>
                                                    <span className="TaskModal__link-result-title">{result.title}</span>
                                                    <span className="TaskModal__linked-board">{result.board_name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {showParentDropdown && !parentSearchLoading && parentSearch.length >= 2 && parentSearchResults.length === 0 && (
                                        <div className="TaskModal__label-dropdown">
                                            <div className="TaskModal__link-no-results">No tasks found</div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
                )}

                {/* Child Tasks */}
                {isEdit && boardId && apiCall && (
                <div className="TaskModal__section TaskModal__hierarchy-section">
                    <label className="TaskModal__label">
                        Children
                        {children.length > 0 && (
                            <span className="TaskModal__comments-count">{children.length}</span>
                        )}
                    </label>
                    {childrenLoading ? (
                        <div className="TaskModal__comments-loading">
                            <span className="btn-spinner btn-spinner--accent" />
                        </div>
                    ) : children.length === 0 ? (
                        <p className="TaskModal__comments-empty">No child tasks.</p>
                    ) : (
                        <div className="TaskModal__linked-list">
                            {children.map(child => (
                                <div key={child.id} className="TaskModal__linked-item">
                                    <button
                                        className="TaskModal__linked-open"
                                        onClick={() => handleOpenHierarchyTask(child)}
                                        title={`Open ${child.board_task_id} in new tab`}
                                    >
                                        <span className={`TaskModal__hierarchy-type-badge TaskModal__hierarchy-type-badge--${(child.task_type || 'task').toLowerCase()}`}>
                                            {child.task_type || 'Task'}
                                        </span>
                                        <span className="TaskModal__linked-id">{child.board_task_id}</span>
                                        <span className="TaskModal__linked-title">{child.title}</span>
                                        <i className="fas fa-external-link-alt TaskModal__linked-ext" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                )}

                {/* Assignee */}
                {boardMembers.length > 0 && (
                <div className="TaskModal__section">
                    <label className="TaskModal__label">Assignee</label>
                    <select
                        className="TaskModal__select"
                        value={assigneeId}
                        onChange={(e) => { setAssigneeId(e.target.value); markChanged(); }}
                        disabled={readOnly}
                    >
                        <option value="">Unassigned</option>
                        {boardMembers.map(member => (
                            <option key={member.id} value={member.id}>
                                {member.name || member.email}
                            </option>
                        ))}
                    </select>
                </div>
                )}

                {/* Description */}
                <div className="TaskModal__section">
                    <label className="TaskModal__label">Description</label>
                    {!readOnly && editingDesc ? (
                        <textarea
                            className="TaskModal__textarea"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            onBlur={handleSaveDescription}
                            placeholder="Add a description..."
                            rows={4}
                            autoFocus
                        />
                    ) : (
                        <div
                            className="TaskModal__description-display"
                            onClick={() => !readOnly && setEditingDesc(true)}
                            style={readOnly ? { cursor: 'default' } : {}}
                        >
                            {description || (readOnly ? 'No description' : 'Click to add a description...')}
                        </div>
                    )}
                </div>

                {/* Priority */}
                {!isSubmit && (
                <div className="TaskModal__section">
                    <label className="TaskModal__label">Priority</label>
                    <div className="TaskModal__priority-options">
                        {PRIORITY_OPTIONS.map(opt => (
                            <button
                                key={opt.value}
                                className={`TaskModal__priority-btn TaskModal__priority-btn--${opt.value.toLowerCase()} ${
                                    priority === opt.value ? 'active' : ''
                                }`}
                                onClick={() => !readOnly && handlePriorityChange(opt.value)}
                                disabled={readOnly}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
                )}

                {/* Due Date */}
                {!isSubmit && (
                <div className="TaskModal__section">
                    <label className="TaskModal__label">Due Date</label>
                    <input
                        className="TaskModal__date-input"
                        type="datetime-local"
                        value={dueDate}
                        onChange={handleDueDateChange}
                        disabled={readOnly}
                    />
                </div>
                )}

                {/* Labels */}
                {!isSubmit && (
                <div className="TaskModal__section">
                    <label className="TaskModal__label">Labels</label>
                    <div className="TaskModal__labels">
                        {labels.map((label, i) => (
                            <span key={i} className="TaskModal__label-tag">
                                {label}
                                {!readOnly && <button onClick={() => handleRemoveLabel(label)}>×</button>}
                            </span>
                        ))}
                    </div>
                    {!readOnly && (
                    <div className="TaskModal__label-input-row">
                        <div className="TaskModal__label-input-wrapper">
                            <input
                                className="TaskModal__label-input"
                                value={labelInput}
                                onChange={(e) => {
                                    setLabelInput(e.target.value);
                                    setHighlightedIndex(-1);
                                }}
                                placeholder="Add a label..."
                                onKeyDown={handleLabelKeyDown}
                                onFocus={() => setShowDropdown(true)}
                                onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                                autoComplete="off"
                            />
                            {showDropdown && filteredSuggestions.length > 0 && (
                                <div className="TaskModal__label-dropdown">
                                    {filteredSuggestions.map((suggestion, i) => (
                                        <div
                                            key={suggestion}
                                            className={`TaskModal__label-dropdown-item${i === highlightedIndex ? ' TaskModal__label-dropdown-item--highlighted' : ''}`}
                                            onMouseDown={() => handleSelectSuggestion(suggestion)}
                                        >
                                            {suggestion}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button className="TaskModal__label-add" onClick={handleAddLabel}>Add</button>
                    </div>
                    )}
                </div>
                )}

                {/* Linked Tasks */}
                {isEdit && boardId && apiCall && (
                <div className="TaskModal__section TaskModal__links-section">
                    <label className="TaskModal__label">
                        Linked Tasks
                        {linkedTasks.length > 0 && (
                            <span className="TaskModal__comments-count">{linkedTasks.length}</span>
                        )}
                    </label>

                    {linksLoading ? (
                        <div className="TaskModal__comments-loading">
                            <span className="btn-spinner btn-spinner--accent" />
                        </div>
                    ) : (
                        <>
                            {linkedTasks.length === 0 && (
                                <p className="TaskModal__comments-empty">No linked tasks.</p>
                            )}
                            <div className="TaskModal__linked-list">
                                {linkedTasks.map(lt => (
                                    <div key={lt.id} className="TaskModal__linked-item">
                                        <button
                                            className="TaskModal__linked-open"
                                            onClick={() => handleOpenLinkedTask(lt)}
                                            title={`Open ${lt.board_task_id} in new tab`}
                                        >
                                            <span className="TaskModal__linked-id">{lt.board_task_id}</span>
                                            <span className="TaskModal__linked-title">{lt.title}</span>
                                            <span className="TaskModal__linked-board">{lt.board_name}</span>
                                            <i className="fas fa-external-link-alt TaskModal__linked-ext" />
                                        </button>
                                        {!readOnly && (
                                            <button
                                                className="TaskModal__linked-remove"
                                                onClick={() => handleRemoveLink(lt.id)}
                                                disabled={unlinkingId === lt.id}
                                                title="Remove link"
                                            >
                                                {unlinkingId === lt.id
                                                    ? <span className="btn-spinner" style={{ width: 10, height: 10 }} />
                                                    : <i className="fas fa-times" />}
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {!readOnly && (
                                <div className="TaskModal__link-input-wrapper">
                                    <input
                                        className="TaskModal__label-input"
                                        value={linkSearch}
                                        onChange={(e) => {
                                            setLinkSearch(e.target.value);
                                            setShowLinkDropdown(true);
                                        }}
                                        placeholder="Search tasks to link..."
                                        onFocus={() => setShowLinkDropdown(true)}
                                        onBlur={() => setTimeout(() => setShowLinkDropdown(false), 200)}
                                        autoComplete="off"
                                        disabled={linking}
                                    />
                                    {linkSearchLoading && (
                                        <span className="TaskModal__link-search-spinner btn-spinner btn-spinner--accent" />
                                    )}
                                    {showLinkDropdown && linkSearchResults.length > 0 && (
                                        <div className="TaskModal__label-dropdown">
                                            {linkSearchResults.map(result => (
                                                <div
                                                    key={result.id}
                                                    className="TaskModal__link-dropdown-item"
                                                    onMouseDown={() => handleAddLink(result)}
                                                >
                                                    <span className="TaskModal__linked-id">{result.board_task_id}</span>
                                                    <span className="TaskModal__link-result-title">{result.title}</span>
                                                    <span className="TaskModal__linked-board">{result.board_name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {showLinkDropdown && !linkSearchLoading && linkSearch.length >= 2 && linkSearchResults.length === 0 && (
                                        <div className="TaskModal__label-dropdown">
                                            <div className="TaskModal__link-no-results">No tasks found</div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
                )}

                {/* Attachments */}
                {isEdit && boardId && apiCall && (
                <div className="TaskModal__section TaskModal__attachments-section">
                    <label className="TaskModal__label">
                        Attachments
                        {attachments.length > 0 && (
                            <span className="TaskModal__comments-count">{attachments.length}</span>
                        )}
                    </label>

                    {attachmentsLoading ? (
                        <div className="TaskModal__comments-loading">
                            <span className="btn-spinner btn-spinner--accent" />
                        </div>
                    ) : (
                        <>
                            {attachments.length === 0 && (
                                <p className="TaskModal__comments-empty">No attachments.</p>
                            )}
                            <div className="TaskModal__attachment-list">
                                {attachments.map(att => (
                                    <div key={att.id} className="TaskModal__attachment-item">
                                        <button
                                            className="TaskModal__attachment-main"
                                            onClick={() => handleDownloadAttachment(att)}
                                            title={`Download ${att.file_name}`}
                                        >
                                            {att.is_image && thumbnails[att.id] ? (
                                                <img
                                                    className="TaskModal__attachment-thumb"
                                                    src={thumbnails[att.id]}
                                                    alt={att.file_name}
                                                />
                                            ) : (
                                                <span className="TaskModal__attachment-icon">
                                                    <i className={`fas ${iconForAttachment(att)}`} />
                                                </span>
                                            )}
                                            <span className="TaskModal__attachment-info">
                                                <span className="TaskModal__attachment-name">{att.file_name}</span>
                                                <span className="TaskModal__attachment-meta">{formatFileSize(att.file_size)}</span>
                                            </span>
                                            <i className="fas fa-download TaskModal__attachment-download" />
                                        </button>
                                        {!readOnly && (
                                            <button
                                                className="TaskModal__linked-remove"
                                                onClick={() => handleDeleteAttachment(att.id)}
                                                disabled={deletingAttachmentId === att.id}
                                                title="Delete attachment"
                                            >
                                                {deletingAttachmentId === att.id
                                                    ? <span className="btn-spinner" style={{ width: 10, height: 10 }} />
                                                    : <i className="fas fa-times" />}
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {attachmentError && (
                                <p className="TaskModal__attachment-error">{attachmentError}</p>
                            )}

                            {!readOnly && (
                                <div className="TaskModal__attachment-upload">
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        style={{ display: 'none' }}
                                        onChange={handleFileSelected}
                                    />
                                    <button
                                        className="TaskModal__attachment-add"
                                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                        disabled={uploadingAttachment}
                                    >
                                        {uploadingAttachment
                                            ? <><span className="btn-spinner" /> Uploading...</>
                                            : <><i className="fas fa-paperclip" /> Add attachment</>}
                                    </button>
                                    <button
                                        className="TaskModal__attachment-add"
                                        onClick={() => setCcPickerOpen(true)}
                                        disabled={uploadingAttachment}
                                        title="Pick a file from your Command Center library"
                                    >
                                        <i className="fas fa-box-open" /> From Command Center
                                    </button>
                                    <span className="TaskModal__attachment-hint">Max 25MB · images, PDF, zip, docs</span>
                                </div>
                            )}
                            {ccPickerOpen && apiCall && (
                                <CommandCenterPickerModal
                                    apiCall={apiCall}
                                    onClose={() => setCcPickerOpen(false)}
                                    onSelect={(file) => uploadAttachmentFile(file)}
                                />
                            )}
                        </>
                    )}
                </div>
                )}

                {/* Comments */}
                {isEdit && boardId && apiCall && (
                <div className="TaskModal__section TaskModal__comments-section">
                    <label className="TaskModal__label">
                        Comments
                        {comments.length > 0 && (
                            <span className="TaskModal__comments-count">{comments.length}</span>
                        )}
                    </label>

                    {commentsLoading ? (
                        <div className="TaskModal__comments-loading">
                            <span className="btn-spinner btn-spinner--accent" />
                        </div>
                    ) : (
                        <>
                            {comments.length === 0 && (
                                <p className="TaskModal__comments-empty">No comments yet.</p>
                            )}
                            <div className="TaskModal__comments-list">
                                {comments.map(comment => (
                                    <div key={comment.id} className="TaskModal__comment">
                                        <div className="TaskModal__comment-avatar">
                                            {(comment.author_name || '?')[0].toUpperCase()}
                                        </div>
                                        <div className="TaskModal__comment-body">
                                            <div className="TaskModal__comment-meta">
                                                <span className="TaskModal__comment-author">
                                                    {comment.author_name || 'Unknown'}
                                                </span>
                                                <span className="TaskModal__comment-time">
                                                    {formatCommentDate(comment.created_at)}
                                                </span>
                                                {(comment.author_uid === currentUserUid || !readOnly) && (
                                                    <button
                                                        className="TaskModal__comment-delete"
                                                        onClick={() => handleDeleteComment(comment.id)}
                                                        disabled={deletingCommentId === comment.id}
                                                        title="Delete comment"
                                                    >
                                                        {deletingCommentId === comment.id
                                                            ? <span className="btn-spinner" style={{ width: 10, height: 10 }} />
                                                            : <i className="fas fa-times" />}
                                                    </button>
                                                )}
                                            </div>
                                            <p className="TaskModal__comment-content">{comment.content}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {canComment && (
                                <div className="TaskModal__comment-form">
                                    <textarea
                                        className="TaskModal__comment-input"
                                        value={commentInput}
                                        onChange={(e) => setCommentInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                                e.preventDefault();
                                                handleSubmitComment();
                                            }
                                        }}
                                        placeholder="Add a comment... (Ctrl+Enter to submit)"
                                        rows={2}
                                        maxLength={2000}
                                        disabled={submittingComment}
                                    />
                                    <button
                                        className="TaskModal__comment-submit"
                                        onClick={handleSubmitComment}
                                        disabled={!commentInput.trim() || submittingComment}
                                    >
                                        {submittingComment
                                            ? <><span className="btn-spinner" /> Posting...</>
                                            : 'Post Comment'}
                                    </button>
                                </div>
                            )}

                            {showSignInToComment && (
                                <p className="TaskModal__comments-signin">
                                    <i className="fas fa-lock" /> Sign in to leave a comment.
                                </p>
                            )}
                        </>
                    )}
                </div>
                )}

                {/* Save Button */}
                {isEdit && !readOnly && (
                <div className="TaskModal__section TaskModal__save-section">
                    <button
                        className={`TaskModal__save-btn ${hasChanges ? 'TaskModal__save-btn--active' : ''}`}
                        onClick={handleSaveAll}
                        disabled={!hasChanges || saving}
                    >
                        {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'No Changes'}
                    </button>
                </div>
                )}

                {/* Submit for review (submit mode) */}
                {isSubmit && (
                <div className="TaskModal__section TaskModal__save-section">
                    {submitDone ? (
                        <div className="TaskModal__submit-success">
                            <i className="fas fa-check-circle" />
                            <span>Submitted for review. A board member will take a look soon.</span>
                            <button className="TaskModal__submit-close" onClick={onClose}>Close</button>
                        </div>
                    ) : (
                        <button
                            className={`TaskModal__save-btn ${title.trim() ? 'TaskModal__save-btn--active' : ''}`}
                            onClick={handleSubmitProposal}
                            disabled={!title.trim() || actionLoading}
                        >
                            {actionLoading ? 'Submitting...' : 'Submit for Review'}
                        </button>
                    )}
                </div>
                )}

                {/* Approve / Deny (review mode) */}
                {isReview && (
                <div className="TaskModal__section TaskModal__review-section">
                    <label className="TaskModal__label">Reason (optional, shown to submitter on denial)</label>
                    <textarea
                        className="TaskModal__textarea"
                        value={reviewNote}
                        onChange={(e) => setReviewNote(e.target.value)}
                        placeholder="Add a note..."
                        rows={2}
                        maxLength={2000}
                        disabled={actionLoading}
                    />
                    <div className="TaskModal__review-actions">
                        <button
                            className="TaskModal__deny-btn"
                            onClick={handleDeny}
                            disabled={actionLoading}
                        >
                            {actionLoading ? '...' : 'Deny'}
                        </button>
                        <button
                            className="TaskModal__approve-btn"
                            onClick={handleApprove}
                            disabled={!title.trim() || !columnId || actionLoading}
                        >
                            {actionLoading ? 'Working...' : 'Approve & Add to Board'}
                        </button>
                    </div>
                </div>
                )}

                {/* Archive & Delete */}
                {isEdit && !readOnly && onArchive && !task.is_archived && (
                <div className="TaskModal__section">
                    <button
                        className="TaskModal__archive-btn"
                        onClick={() => onArchive(task.id)}
                    >
                        <i className="fas fa-archive" /> Archive Task
                    </button>
                </div>
                )}
                {isEdit && !readOnly && onDelete && (
                <div className="TaskModal__section TaskModal__danger">
                    <button
                        className="TaskModal__delete-btn"
                        disabled={deleting}
                        onClick={async () => {
                            if (window.confirm('Delete this task?')) {
                                setDeleting(true);
                                await onDelete(task.id);
                            }
                        }}
                    >
                        {deleting ? 'Deleting...' : 'Delete Task'}
                    </button>
                </div>
                )}

                {/* Timestamps */}
                {!isSubmit && task.created_at && (
                <div className="TaskModal__timestamps">
                    <span>{isReview ? 'Submitted' : 'Created'} {new Date(task.created_at).toLocaleDateString()}</span>
                    {task.updated_at && <span>Updated {new Date(task.updated_at).toLocaleDateString()}</span>}
                </div>
                )}
            </div>
        </div>
    );
};

export default TaskModal;
